import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Mail, Loader2, Archive, Inbox, RotateCcw } from "lucide-react";
import { AdminNav } from "../components/site/AdminNav";
import { toast } from "sonner";
import { WhatsAppIcon } from "../components/site/Icons";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../lib/auth";
import { api, apiError } from "../lib/api";
import { RefundItem } from "../components/traffic/RefundsPanel";

const RESULT = {
  reserva_anulada: "Era una reserva sin pagar: se anuló sola, no hay dinero para devolver.",
  sin_devolucion: "Según la anticipación no corresponde devolución.",
  ya_viajo: "El pasajero ya subió al micro.",
  no_aplica: "El pasaje no está activo (vencido o anulado).",
};

const WithdrawalBox = ({ code, result }) => {
  const [b, setB] = useState(null);
  const load = () => { api.get(`/admin/withdrawals/${code}`).then((r) => setB(r.data ?? false)).catch(() => setB(false)); };
  useEffect(() => { load(); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps
  if (b === null) return <Loader2 className="mt-4 h-4 w-4 animate-spin" />;
  return (
    <div data-testid={`withdrawal-box-${code}`} className="mt-4 rounded-2xl bg-[var(--paper)] ring-1 ring-black/5">
      <p className="px-4 pt-3 font-mono text-[11px] uppercase tracking-widest text-[var(--forest)]">Arrepentimiento · pasaje {code}</p>
      {b?.refund?.status ? <RefundItem b={b} onDone={load} /> : <p data-testid={`withdrawal-status-${code}`} className="p-4 text-sm">{RESULT[result] ?? (b ? `Estado del pasaje: ${b.status}` : "Pasaje no encontrado")}</p>}
    </div>
  );
};

const TABS = [["nuevo", "Nuevos"], ["respondido", "Respondidos"], ["archivado", "Archivados"]];
const fmt = (iso) => new Date(iso).toLocaleString("es-AR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const waPhone = (p) => {
  const d = (p || "").replace(/\D/g, "");
  if (!d) return "";
  return d.startsWith("54") ? d : `549${d.replace(/^0/, "")}`;
};

const MessageCard = ({ m, onUpdate }) => {
  const [reply, setReply] = useState(`Hola ${m.name.split(" ")[0]}, gracias por escribirnos.\n\n`);
  const mailto = `mailto:${m.email}?subject=${encodeURIComponent(`Re: tu consulta sobre ${m.topic} — Santiagueño`)}&body=${encodeURIComponent(reply)}`;
  const wa = waPhone(m.phone);
  const send = (via, url) => {
    window.open(url, "_blank", "noopener");
    onUpdate(m.id, "respondido", via);
  };

  return (
    <article data-testid={`admin-message-${m.id}`} className="rounded-[24px] bg-white p-6 ring-1 ring-black/5 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-display-up text-xl">{m.name}</p>
          <p className="text-sm text-[var(--muted-ink)]">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p>
        </div>
        <div className="text-right">
          <span className="rounded-full bg-[var(--paper)] px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-[var(--forest)]">{m.topic}</span>
          <p className="mt-2 font-mono text-xs text-[var(--muted-ink)]">{fmt(m.created_at)}</p>
        </div>
      </div>
      <p className="mt-5 whitespace-pre-wrap text-[15px]">{m.message}</p>
      {m.withdrawal_code && <WithdrawalBox code={m.withdrawal_code} result={m.withdrawal_result} />}
      {m.status === "respondido" && <p className="mt-4 text-xs font-semibold text-[var(--forest)]">Respondido por {m.replied_via} · {m.replied_at ? fmt(m.replied_at) : ""}</p>}
      <Textarea data-testid={`admin-reply-input-${m.id}`} value={reply} onChange={(e) => setReply(e.target.value)} rows={3} className="mt-5 rounded-xl" />
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={() => send("email", mailto)} data-testid={`admin-reply-email-${m.id}`} className="inline-flex items-center gap-2 rounded-full bg-[var(--forest)] px-5 py-2.5 text-sm font-bold text-white hover:bg-[var(--forest-2)]"><Mail className="h-4 w-4" /> Responder por email</button>
        {wa && <button onClick={() => send("WhatsApp", `https://wa.me/${wa}?text=${encodeURIComponent(reply)}`)} data-testid={`admin-reply-whatsapp-${m.id}`} className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-bold text-[var(--ink)]"><WhatsAppIcon className="h-4 w-4" /> Responder por WhatsApp</button>}
        {m.status !== "archivado"
          ? <button onClick={() => onUpdate(m.id, "archivado")} data-testid={`admin-archive-${m.id}`} className="inline-flex items-center gap-2 rounded-full border border-black/10 px-5 py-2.5 text-sm font-semibold"><Archive className="h-4 w-4" /> Archivar</button>
          : <button onClick={() => onUpdate(m.id, "nuevo")} data-testid={`admin-restore-${m.id}`} className="inline-flex items-center gap-2 rounded-full border border-black/10 px-5 py-2.5 text-sm font-semibold"><RotateCcw className="h-4 w-4" /> Volver a nuevos</button>}
      </div>
    </article>
  );
};

export default function AdminMensajes() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("nuevo");

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    api.get("/admin/messages").then((r) => { if (alive) setItems(Array.isArray(r.data) ? r.data : []); }).catch((e) => toast.error(apiError(e)));
    return () => { alive = false; };
  }, [user]);

  const counts = useMemo(() => Object.fromEntries(TABS.map(([k]) => [k, (items ?? []).filter((m) => m.status === k).length])), [items]);

  if (user === null) return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (user === false) return <Navigate to="/admin" replace />;
  if (user.role !== "admin") return <Navigate to="/admin/trafico" replace />;

  const update = async (id, status, via) => {
    try {
      const { data } = await api.patch(`/admin/messages/${id}`, { status, replied_via: via });
      setItems((prev) => prev.map((m) => (m.id === id ? data : m)));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const list = (items ?? []).filter((m) => m.status === tab);

  return (
    <section data-testid="admin-messages-page" className="min-h-[75vh] bg-[var(--paper)] pb-24">
      <div className="mx-auto max-w-[1000px] px-5 pt-12 md:px-10">
        <AdminNav title="Mensajes" />
        <div className="mt-8 flex flex-wrap gap-2">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${tab === k ? "bg-[var(--ink)] text-white" : "bg-white"}`}>{l} <span className="ml-1 opacity-70">{counts[k] ?? 0}</span></button>
          ))}
        </div>
        <div className="mt-6 space-y-4">
          {items === null && <div className="flex items-center gap-2 text-[var(--muted-ink)]"><Loader2 className="h-5 w-5 animate-spin" /> Cargando…</div>}
          {items !== null && list.length === 0 && <div data-testid="admin-empty" className="flex flex-col items-center rounded-[24px] bg-white py-16 text-[var(--muted-ink)]"><Inbox className="h-8 w-8" /><p className="mt-3">No hay mensajes acá.</p></div>}
          {list.map((m) => <MessageCard key={m.id} m={m} onUpdate={update} />)}
        </div>
      </div>
    </section>
  );
}
